const DB_NAME = 'sharethedeal-offline';
const DB_VERSION = 2;

/**
 * Opens (or creates) the IndexedDB database.
 * Defines three object stores:
 *   - discounts: cached discount list items
 *   - favorites: cached favorites list
 *   - pendingActions: write operations queued while offline
 */
export function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      if (!db.objectStoreNames.contains('discounts')) {
        const discountStore = db.createObjectStore('discounts', { keyPath: 'discount_id' });
        discountStore.createIndex('created_at', 'created_at', { unique: false });
      }

      if (!db.objectStoreNames.contains('favorites')) {
        db.createObjectStore('favorites', { keyPath: 'discount_id' });
      }

      // Stores actions (add favorite, rate, etc.) that failed due to being offline.
      // These are replayed automatically when the user comes back online.
      if (!db.objectStoreNames.contains('pendingActions')) {
        db.createObjectStore('pendingActions', {
          keyPath: 'id',
          autoIncrement: true,
        });
      }

      // Stores pre-fetched form dropdown data (categories, cities, areas, stores)
      // keyed by a string identifier, e.g. 'dropdown_el' or 'dropdown_en'
      if (!db.objectStoreNames.contains('formData')) {
        db.createObjectStore('formData', { keyPath: 'key' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// -----------------------------------------------------------------------
// DISCOUNTS
// -----------------------------------------------------------------------

/**
 * Saves an array of discount objects into the local cache.
 * Uses put() so existing entries are updated rather than duplicated.
 */
export async function saveDiscounts(discounts) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('discounts', 'readwrite');
    const store = tx.objectStore('discounts');
    discounts.forEach((d) => store.put(d));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Returns all cached discounts.
 */
export async function getCachedDiscounts() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('discounts', 'readonly');
    const req = tx.objectStore('discounts').getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// -----------------------------------------------------------------------
// FAVORITES
// -----------------------------------------------------------------------

/**
 * Replaces the entire favorites cache with the provided array.
 */
export async function saveFavorites(favorites) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('favorites', 'readwrite');
    const store = tx.objectStore('favorites');
    store.clear();
    favorites.forEach((f) => store.put(f));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Returns all cached favorites.
 */
export async function getCachedFavorites() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('favorites', 'readonly');
    const req = tx.objectStore('favorites').getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// -----------------------------------------------------------------------
// PENDING ACTIONS (offline write queue)
// -----------------------------------------------------------------------

/**
 * Queues a write action to be replayed when the user comes back online.
 * @param {object} action - e.g. { type: 'ADD_FAVORITE', url, method, body, accessToken }
 */
export async function queuePendingAction(action) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('pendingActions', 'readwrite');
    tx.objectStore('pendingActions').add({ ...action, timestamp: Date.now() });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Returns all queued pending actions.
 */
export async function getPendingActions() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('pendingActions', 'readonly');
    const req = tx.objectStore('pendingActions').getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Removes a pending action after it has been successfully replayed.
 * @param {number} id - the auto-incremented key of the action to remove
 */
export async function deletePendingAction(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('pendingActions', 'readwrite');
    tx.objectStore('pendingActions').delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// -----------------------------------------------------------------------
// FORM DROPDOWN DATA (pre-fetched at login for offline AddDiscount use)
// -----------------------------------------------------------------------

/**
 * Saves pre-fetched dropdown data under a given key.
 * @param {string} key  - e.g. 'dropdown_el' or 'dropdown_en'
 * @param {object} data - { categories: [...], stores: [...] }
 */
export async function saveFormData(key, data) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('formData', 'readwrite');
    tx.objectStore('formData').put({ key, data });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Returns previously saved dropdown data for the given key, or null if not found.
 * @param {string} key - e.g. 'dropdown_el'
 */
export async function getFormData(key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('formData', 'readonly');
    const req = tx.objectStore('formData').get(key);
    req.onsuccess = () => resolve(req.result?.data || null);
    req.onerror = () => reject(req.error);
  });
}
